/**
 * card_normals.ts — the groundcover CARDS lit like the ground they stand on (surfaces, 2026-09-27, session 4b).
 *
 * WHY. `tools/asset-pipeline/bake-cards.mjs` stands a plant's rendered views up as crossed VERTICAL quads whose vertex
 * normals are horizontal (the card's facing), with the plant's own normals in a tangent-space map. Under a high sun a
 * horizontal normal takes almost no direct light (N·L → 0 at noon) and only half the sky, so reeds, tufts and ferns drew
 * near-black against a bright beach (the water probe's shore pictures, 2026-09-27: dark clumps in the swash). A real
 * thin plant is lit from above through and around its blades; the standard real-time answer is to BEND the shading
 * normal toward the sky (grass normals pointing up; SpeedTree's normal bending). The bend is done in the SHADER, after
 * three's back-face flip and after the normal map (`materialNormal` inside the material's normalNode — three builds
 * it in the NORMAL sub-build where `normalView` is already the flipped geometric normal), so a double-sided card's back
 * face bends UP as well; a CPU bend of the vertex normals would have flipped DOWN on every back face.
 *
 * One uniform (`uCardBend`) drives every card material — the A/B lever (`__cardBend(k)`; 0 = the cooked normals
 * exactly). The card materials share ONE program key (their graphs are identical — THE CLASSIC-KEY LAW: a classic
 * material's node graph is not part of three's key, so they must not share a key with the plain groundcover).
 */
import type * as THREE from 'three/webgpu';
import { Fn, normalize, vec4, cameraViewMatrix, materialNormal, uniform } from 'three/tsl';

/** The share of the world's UP added to a card's shading normal before it is re-normalised (0 = none). */
export const CARD_BEND_DEFAULT = 0.6;
export const uCardBend = uniform(CARD_BEND_DEFAULT);

/** A cooked card: a handful of triangles, most vertices with a horizontal normal (the vertical quads). */
export function isCardGeometry(g: THREE.BufferGeometry): boolean {
  const n = g.getAttribute('normal') as THREE.BufferAttribute | undefined;
  const p = g.getAttribute('position') as THREE.BufferAttribute | undefined;
  if (!n || !p) return false;
  const idx = g.getIndex();
  const tris = idx ? idx.count / 3 : p.count / 3;
  if (tris > 16 || n.count < 4) return false;
  let horizontal = 0;
  for (let i = 0; i < n.count; i++) if (Math.abs(n.getY(i)) < 0.05) horizontal++;
  return horizontal >= n.count * 0.5;
}

/** CPU mirror of the bend (world frame): normalize(n + up·k). */
export function cardBendCpu(n: readonly [number, number, number], k: number): [number, number, number] {
  const x = n[0], y = n[1] + k, z = n[2];
  const l = Math.hypot(x, y, z) || 1;
  return [x / l, y / l, z / l];
}

/** The program key every bent card material shares (its graph depends only on these switches). */
export function cardProgramKey(wind: boolean, trample: boolean, cloudShadows: boolean): string {
  return `foliage:card-bent:v1|w${wind ? 1 : 0}|t${trample ? 1 : 0}|c${cloudShadows ? 1 : 0}`;
}

/** Bend a card material's shading normal toward the sky (view space: the camera's view of world up). */
export function applyCardNormalBend(material: THREE.Material): void {
  (material as unknown as { normalNode: unknown }).normalNode = Fn(() => normalize(materialNormal.add(cameraViewMatrix.mul(vec4(0, 1, 0, 0)).xyz.mul(uCardBend))))();
  material.needsUpdate = true;
}
